import { AfterViewInit, Directive } from '@angular/core';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { Adyen } from '../../adyen.namespace';
import { AbstractAdyenCardComponent } from '../abstract-adyen-card.component';

@Directive()
export abstract class AdyenSecuredFieldsComponentDef extends AbstractAdyenCardComponent implements AfterViewInit {
    /**
     * Payment method code
     * @type {string}
     */
    protected readonly methodCode = Adyen.PaymentMethod.CreditCard;

    /**
     * Selector for component container
     * @type {string}
     */
    protected containerSelector = '#adyen-cards-container';

    ngAfterViewInit() {
        super.ngAfterViewInit();
        if (this.containerElement) {
            this.subscriptions.add(this.app.events.on(Checkout.EVENTS.PaymentMethodsUpdate).subscribe(() => this.onPaymentsChange()));
        }
    }

    ////////////////////////////////////////////
    ////////
    ////////    Payment method methods
    ////////
    ////////////////////////////////////////////

    /**
     * If we should display save card checkbox in component
     * @type {boolean}
     */
    protected get saveCard(): boolean {
        return this.isVault && this.app.customer.model.isLoggedIn;
    }

    /**
     * If saving cards is enabled
     * @type {boolean}
     */
    protected get isVault(): boolean {
        return !!this.method.isVault;
    }

    protected onPaymentsChange(): void {
        if (this.component && this.component.props.enableStoreDetails !== this.saveCard) {
            this.initComponent();
        }
    }

    ////////////////////////////////////////////
    ////////
    ////////    Cards component methods
    ////////
    ////////////////////////////////////////////

    /**
     * Returns configuration options object for card component
     * @returns {Adyen.ICardComponentOptions}
     */
    protected getComponentOptions(): Adyen.ICardComponentOptions | undefined {
        const options = {
            onChange: (state: Adyen.ICardComponentState, component) => this.onCardComponentChange(state, component),
            groupTypes: this.getAllowedTypes()
        } as Adyen.ICardComponentOptions;
        if (this.saveCard) {
            options.enableStoreDetails = true;
        }
        return options;
    }

    /**
     * Returns allowed group types for card component
     * @returns {string[]}
     */
    protected getAllowedTypes(): string[] {
        return this.method.availableTypes?.map(i => i.toLowerCase()) || ['mc', 'visa', 'amex'];
    }

    ////////////////////////////////////////////
    ////////
    ////////    Checkout callback
    ////////
    ////////////////////////////////////////////

    /**
     * Abstract method letting you plug in your action before checkout submission
     * @returns {false | void} False if can't continue
     */
    protected onCheckoutSubmit(checkoutSubmitBeforeEventData: Checkout.ISubmitBeforeEventData): false | void {
        if (super.onCheckoutSubmit(checkoutSubmitBeforeEventData) === false) {
            return false;
        }

        const paymentSubmissionData = this.getPaymentData();
        if (paymentSubmissionData) {
            checkoutSubmitBeforeEventData.data.paymentMethod.additional_data = {
                ...(paymentSubmissionData.additional_data || {}),
                ...this.adyen.getBrowserInfo()
            };
            checkoutSubmitBeforeEventData.data.paymentMethod.method = paymentSubmissionData.method;
        } else {
            this.showValidation();
            checkoutSubmitBeforeEventData.continueSubmission = false;
            this.scrollTo(this.getContainerElement());
        }
    }

    /**
     * Returns payment data for checkout submission
     * @returns {Adyen.IPaymentSubmissionData<Adyen.ICardSubmissionData> | undefined}
     */
    protected getPaymentData(): Adyen.IPaymentSubmissionData<Adyen.ICardSubmissionData> | undefined {
        const paymentData: Adyen.ICardComponentData | undefined = this.getComponentData();

        if (!this.isValid() || !paymentData) {
            return undefined;
        }

        const additionalData: Adyen.ICardSubmissionData = {
            number: <string>paymentData.encryptedCardNumber,
            cvc: <string>paymentData.encryptedSecurityCode,
            expiryMonth: <string>paymentData.encryptedExpiryMonth,
            expiryYear: <string>paymentData.encryptedExpiryYear
        };

        if (paymentData.holderName) {
            additionalData.holderName = paymentData.holderName;
        }
        const cardBrand = this.getCardBrand();
        if (cardBrand) {
            additionalData.cc_type = cardBrand;
        }
        if (this.component) {
            additionalData.store_cc = this.component.state.storePaymentMethod;
        }
        return { method: this.methodCode, additional_data: additionalData };
    }
}